import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import backOfficeRoutes from 'routesBackOffice';

// Navigation du back-office : les routes `menu: true` sans `section` sont des liens directs, les
// autres sont rangées dans ces sections (menus déroulants), dans cet ordre. Les pages d'un même
// `tabGroup` partagent une entrée de menu et s'affichent en onglets (PageTabs).
export const BACK_OFFICE_SECTIONS: { key: string; label: string }[] = [
    { key: 'leads', label: 'Suivi des leads' },
    { key: 'demand', label: 'Demande' },
    { key: 'sources', label: 'Sources' },
    { key: 'catalog', label: 'Catalogue et données' },
    { key: 'finance', label: 'Finance' },
    { key: 'growth', label: 'Croissance' },
];

const fullPath = (route: RoutesType) => `${route.layout}${route.path}`;

// Page exacte ou sous-page (/offers/marketplace/123 -> Offres), sans que /leads n'allume /leads-kpi.
export const isPathActive = (pathname: string, route: RoutesType) => {
    const path = fullPath(route).split('/:')[0];
    return pathname === path || pathname.startsWith(`${path}/`);
};

// Une entrée de menu est active si sa page, ou une page de son groupe d'onglets, est ouverte.
export const isMenuEntryActive = (pathname: string, route: RoutesType) =>
    route.tabGroup
        ? backOfficeRoutes.some((r) => r.tabGroup === route.tabGroup && isPathActive(pathname, r))
        : isPathActive(pathname, route);

// Pages d'un groupe d'onglets : l'entrée du menu d'abord, puis les autres dans l'ordre des routes.
const groupRoutes = (tabGroup: string) =>
    backOfficeRoutes
        .filter((r) => r.tabGroup === tabGroup)
        .sort((a, b) => Number(!!b.menu) - Number(!!a.menu));

export function PageTabs({ tabGroup, children }: { tabGroup: string; children: React.ReactNode }) {
    const { pathname } = useLocation();
    return (
        <div className="pt-[50px]">
            <div className="mb-5 flex gap-2 border-b border-gray-200 dark:border-navy-600">
                {groupRoutes(tabGroup).map((route) => {
                    const active = isPathActive(pathname, route);
                    return (
                        <Link
                            key={route.path}
                            to={fullPath(route)}
                            className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${
                                active
                                    ? 'border-brand-500 text-brand-500'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                            }`}
                        >
                            {route.tabLabel || route.name}
                        </Link>
                    );
                })}
            </div>
            {/* Les pages ont leur propre pt-[50px], déjà assuré au-dessus des onglets. */}
            <div className="[&>div:first-child]:!pt-0">{children}</div>
        </div>
    );
}

// Compteurs affichés dans le menu (`badge` d'une route) : choses à traiter. Rechargés à chaque
// changement de page ; le back les met en cache.
export function useMenuBadges(): Record<string, number> {
    const { pathname } = useLocation();
    const [badges, setBadges] = useState<Record<string, number>>({});
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return;
        axios
            .get(`${CONFIG.apiUrl}/industry/mismatches/count`, { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => setBadges((prev) => ({ ...prev, productMismatches: res.data.count || 0 })))
            .catch(() => {});
    }, [pathname]);
    return badges;
}
